import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { uploadMediaFile, describeUploadError, savedSessionFor, sessionStatus, forgetSession, newTraceId } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, Video, Image as ImageIcon, MapPin, Search, Plus } from "lucide-react";

const MAX_BATCH = 12; // per batch for non-admin contributors

const getVideoDuration = (file) =>
  new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      window.URL.revokeObjectURL(v.src);
      resolve(v.duration || 0);
    };
    v.onerror = () => resolve(0);
    v.src = URL.createObjectURL(file);
  });

export const UploadDialog = ({ open, onClose, onUploaded, quick = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [items, setItems] = useState([]); // {id, file, preview, duration, isVideo, title}
  const [description, setDescription] = useState("");
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [attest, setAttest] = useState(false);
  const [locQuery, setLocQuery] = useState("");
  const [locResults, setLocResults] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState(null);     // member: offline city row · admin: full geocoder candidate (same shape the editor saves)
  const [locSearching, setLocSearching] = useState(false);
  const [privacy, setPrivacy] = useState("city");
  const [library, setLibrary] = useState("originals"); // admin: BareFootage Originals (default) · Celebrities · Pornstars
  const [namedSubject, setNamedSubject] = useState("");
  // Subject Type `candid` is an EXPLICIT owner tick only (→ manual_tags). Automatic candid for owner Originals lives in the
  // backend default/AI tag layer (default_tags) and is never inserted into manual_tags — so no viewport pre-ticks it.
  const [candid, setCandid] = useState(false);

  const isAdmin = !!user?.is_admin;
  const cap = isAdmin ? Infinity : MAX_BATCH;

  const [results, setResults] = useState([]);   // admin: persistent post-upload result cards (detected location visible without Save / Candid)
  const reset = () => {
    setResults([]);
    setItems([]); setDescription(""); setAttest(false); setSubmitting(false); setLibrary("originals"); setNamedSubject(""); setCandid(false);
    setProgress({ done: 0, total: 0 });
    setLocQuery(""); setLocResults([]); setSelectedLoc(null); setPrivacy("city");
  };

  const searchLoc = async (q) => {
    setLocQuery(q); setSelectedLoc(null);
    if (isAdmin) return;   // admin: precise candidates are fetched on Enter / the search button (Nominatim is rate-limited)
    if (q.trim().length < 2) { setLocResults([]); return; }
    try { const r = await api.get("/locations/search", { params: { q } }); setLocResults(r.data); } catch {}
  };

  // Admin: THE SAME precise candidate system as the editor (/geo/search → POI / airport (IATA) / street / exact address / area),
  // preserving the exact label and the candidate's own coordinates.
  const searchCandidates = async () => {
    const q = locQuery.trim();
    if (q.length < 2) { setLocResults([]); return; }
    setLocSearching(true);
    try {
      const r = await api.get("/geo/search", { params: { q } });
      const cands = r.data?.candidates || [];
      setLocResults(cands);
      if (!cands.length) toast.message("No place found — try a more specific name");
    } catch (e) { toast.error(`Place search failed${e?.response?.status ? ` (HTTP ${e.response.status})` : ""}`); }
    finally { setLocSearching(false); }
  };
  const pickCandidate = (c) => { setSelectedLoc(c); setLocResults([]); setLocQuery(""); };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const next = [...items];
    for (const f of files) {
      const isVideo = f.type.startsWith("video");
      const isImage = f.type.startsWith("image");
      if (!isVideo && !isImage) { toast.error(`"${f.name}" is not an image or video`); continue; }
      if (next.length >= cap) {
        toast.error(`You can upload up to ${MAX_BATCH} at a time`);
        break;
      }
      let duration = 0;
      if (isVideo) {
        duration = await getVideoDuration(f);
        if (!isAdmin && duration > 31) {
          toast.error(`"${f.name}" is ${Math.round(duration)}s — videos must be 30s or less`);
          continue;
        }
      }
      const item = {
        id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 6)}`,
        file: f, preview: URL.createObjectURL(f), duration, isVideo,
        title: f.name.replace(/\.[^.]+$/, ""),
        traceId: newTraceId(),   // one upload identity per item for the whole single-shot retry sequence (auto retries + Retry button)
      };
      // a previous attempt of this exact file left an open chunked session (even across a page/browser restart) → offer Resume
      const saved = savedSessionFor(f);
      if (saved) {
        try {
          const st = await sessionStatus(saved.uploadId);
          if (st && st.status !== "expired") item.resume = { uploadId: st.upload_id, traceId: st.trace_id, received: (st.received_chunks || []).length, expected: st.expected_chunks, completed: st.status === "completed" && !!st.media_id };
          else forgetSession(f);
        } catch {}
      }
      next.push(item);
    }
    setItems(next);
  };

  const removeItem = (id) => setItems((p) => p.filter((x) => x.id !== id));
  const setTitle = (id, title) => setItems((p) => p.map((x) => (x.id === id ? { ...x, title } : x)));
  const patchItem = (id, patch) => setItems((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const uploadOne = async (it) => {
    const fields = { title: (it.title || it.file.name).trim(), description: description.trim(), duration: String(it.duration), location_privacy: privacy };
    if (isAdmin) Object.assign(fields, { content_library: library, named_subjects: library === "originals" ? "" : namedSubject.trim() });
    if (isAdmin && library === "originals" && candid) fields.tags = "candid";   // explicit owner tick only → normal form tag path → manual_tags
    if (selectedLoc && isAdmin) fields.location_candidate = JSON.stringify(selectedLoc);   // explicit owner selection: exact label + candidate coordinates win over file GPS
    else if (selectedLoc) Object.assign(fields, { location_city: selectedLoc.city, location_region: selectedLoc.region || "", location_country: selectedLoc.country, location_cc: selectedLoc.cc || "" });
    // shared chunk-aware uploader: retries network/5xx with backoff, resumes an open session sending only the missing parts
    return uploadMediaFile(it.file, fields, (pct) => patchItem(it.id, { pct }), {
      resume: it.resume ? { uploadId: it.resume.uploadId, traceId: it.resume.traceId } : undefined,
      onStage: (stage) => patchItem(it.id, { stage }),
      traceId: it.traceId,
    });
  };

  // Upload the given items (all, or one for Resume). Failed items STAY in the dialog with their exact stage/trace so they can be resumed.
  const runUploads = async (targets) => {
    setSubmitting(true);
    setProgress({ done: 0, total: targets.length });
    let ok = 0; let failed = 0; let dup = 0; let videos = 0; const created = []; const dupIds = []; const doneIds = [];
    for (const it of targets) {
      patchItem(it.id, { error: null, stage: "Starting…" });
      try {
        const m = await uploadOne(it);
        if (m?.id) created.push(m.id);
        if (m?.location && !quick) {   // detected (file GPS) or owner-selected location — shown immediately, no Subject Type needed
          const L = m.location; const coords = Number.isFinite(L.lat) ? `${Number(L.lat).toFixed(4)}, ${Number(L.lng).toFixed(4)}` : "";
          const src = L.provenance === "owner_selected" ? "Location (your selection)" : L.provenance === "original_metadata" ? "GPS read from the file (place still resolving)" : "Location detected from the file's GPS";
          const hier = [L.area_context?.name, L.city, L.region, L.country].filter(Boolean).join(" · ");
          toast.message(`${src}: ${L.label || L.display || coords}`, { description: [hier, coords, L.provenance === "owner_selected" ? null : (m.location_public ? "public" : L.state === "needs_verification" ? "visible to you · location needs verification" : "visible to you · public once Subject Type candid is saved")].filter(Boolean).join(" · "),
            duration: 12000, action: { label: "Open", onClick: () => { onClose?.(); navigate(`/browse?open=${m.id}`); } } });
          patchItem(it.id, { detectedLocation: L.label || L.display || coords });
        }
        if (isAdmin && !quick && m?.id) setResults((r) => [...r, { id: m.id, title: it.title, preview: it.preview, isVideo: it.isVideo, media_type: m.media_type, location: m.location || null, location_public: !!m.location_public, polls: 0 }]);
        if (m?.media_type === "video") videos += 1;
        ok += 1; doneIds.push(it.id);
      } catch (e) {
        const status = e?.status;
        if (status === 409 && e?.detail?.existing_id) {
          dup += 1; dupIds.push(e.detail.existing_id); doneIds.push(it.id);   // exact duplicate — nothing uploaded, nothing to retry
          toast.message(`Exact duplicate — nothing uploaded${e.traceId ? ` · Trace ${e.traceId}` : ""}`);
        } else {
          failed += 1;
          const text = describeUploadError(e);
          patchItem(it.id, { error: text, stage: null, resume: e?.resumable && e?.uploadId ? { uploadId: e.uploadId, traceId: e.traceId, received: null } : it.resume });
          toast.error(`"${it.title}" — ${text}`, { duration: 12000 });
        }
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setSubmitting(false);
    setItems((p) => p.filter((x) => !doneIds.includes(x.id)));
    if (dup > 0 && dupIds.length) toast.message(`Exact duplicate — already uploaded (${dup} skipped)`, {
      description: "This exact file is already in BareFootage.",
      action: { label: "Open existing", onClick: () => { onClose?.(); navigate(`/browse?open=${dupIds[0]}`); } },
    });
    if (ok > 0) {
      toast.success(quick
        ? `Uploaded ${ok} ${ok === 1 ? "item" : "items"} — processing, caption on its way`
        : isAdmin
        ? `Upload saved — ${ok} ${ok === 1 ? "item" : "items"} published${videos ? " · video processing runs separately (a processing failure never removes the original)" : ""}`
        : `Submitted ${ok} ${ok === 1 ? "item" : "items"}! Pending review.`);
      onUploaded?.(created);
    }
    return failed;
  };

  const submit = async () => {
    if (!items.length) { toast.error("Add at least one photo or video"); return; }
    if (items.some((x) => !x.title.trim())) { toast.error("Every item needs a title"); return; }
    if (!isAdmin && !attest) { toast.error("Please confirm the upload guidelines"); return; }
    if (isAdmin && library !== "originals" && !namedSubject.trim()) { toast.error(`${library === "celebrities" ? "Celebrities" : "Pornstars"} uploads need a Named Subject`); return; }
    const failed = await runUploads(items);
    if (failed === 0 && (!isAdmin || quick)) { reset(); onClose(); }   // members + owner quick mode: close when everything landed; desktop admin keeps the result cards open
  };

  const resumeOne = async (it) => {
    const failed = await runUploads([it]);
    if (failed === 0 && (!isAdmin || quick) && items.filter((x) => x.id !== it.id).length === 0) { reset(); onClose(); }   // last item landed → close like Submit does
  };

  // READ-ONLY polling of the existing media detail endpoint while place resolution is still pending (never triggers AI or location writes).
  const pollGen = useRef(0);
  useEffect(() => {
    const pending = results.filter((r) => r.location && locPending(r.location) && r.polls < 12);
    if (!open || !pending.length) return;
    const gen = ++pollGen.current;
    const t = setTimeout(async () => {
      for (const r of pending) {
        try {
          const { data } = await api.get(`/media/${r.id}`);
          if (gen !== pollGen.current || !data || data.id !== r.id) continue;
          setResults((prev) => prev.map((x) => (x.id === r.id ? { ...x, location: data.location || x.location, location_public: !!data.location_public, polls: x.polls + 1 } : x)));
        } catch (e) {
          if (gen === pollGen.current) setResults((prev) => prev.map((x) => (x.id === r.id ? { ...x, polls: x.polls + 1 } : x)));
        }
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [results, open]);

  const canAddMore = items.length < cap;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) { reset(); onClose(); } }}>
      <DialogContent data-testid="upload-dialog" className="max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium">{quick ? "Upload" : "Submit your footage"}</DialogTitle>
          <DialogDescription>
            {quick
              ? "Originals · file GPS becomes the exact location automatically · caption and X text generate after processing."
              : isAdmin
              ? "Add as many as you like — your uploads publish instantly as Official."
              : `Batch upload up to ${MAX_BATCH} photos or videos (30s max). Goes live after review.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Result cards live OUTSIDE the items branch: successful items are removed from `items`, so the cards must not depend on it */}
          {results.length > 0 && (
            <div className="space-y-2" data-testid="upload-results">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Uploaded — detected location (no Save or Subject Type needed to see it)</div>
              {results.map((r) => <UploadResultCard key={r.id} r={r} onOpen={() => { onClose?.(); navigate(`/browse?open=${r.id}`); }} />)}
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="secondary" data-testid="upload-results-more" onClick={() => setResults([])}>Upload more</Button>
                <Button type="button" size="sm" data-testid="upload-results-done" onClick={() => { reset(); onClose(); }}>Done</Button>
              </div>
            </div>
          )}
          {items.length === 0 ? (
            <div
              data-testid="upload-dropzone"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={`rounded-lg border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
                dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              }`}
            >
              <UploadCloud className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm">{quick ? <span className="text-primary font-medium">Choose photos or videos</span> : <>Drag & drop or <span className="text-primary font-medium">browse</span> — select multiple</>}</p>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP, MP4, MOV{isAdmin ? "" : ` · up to ${MAX_BATCH} at once`}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-muted-foreground" data-testid="upload-count">
                  {items.length} selected{isAdmin ? "" : ` / ${MAX_BATCH}`}
                </span>
                {canAddMore && (
                  <button data-testid="upload-add-more" onClick={() => inputRef.current?.click()}
                    className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
                    <Plus className="w-3.5 h-3.5" /> Add more
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {items.map((it) => (
                  <div key={it.id} data-testid="upload-thumb" className="space-y-1.5">
                    <div className="relative rounded-lg overflow-hidden border border-border bg-black aspect-square">
                      {it.isVideo ? (
                        <video src={it.preview} className="w-full h-full object-cover" muted />
                      ) : (
                        <img src={it.preview} alt="preview" className="w-full h-full object-cover" />
                      )}
                      <button
                        onClick={() => removeItem(it.id)}
                        data-testid="upload-remove-thumb"
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center hover:bg-destructive transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="absolute top-1.5 left-1.5 flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-black/70">
                        {it.isVideo ? <><Video className="w-2.5 h-2.5" /> {Math.round(it.duration)}s</> : <ImageIcon className="w-2.5 h-2.5" />}
                      </div>
                    </div>
                    {!quick && (
                    <Input
                      data-testid="upload-item-title"
                      value={it.title}
                      onChange={(e) => setTitle(it.id, e.target.value)}
                      placeholder="Title"
                      className="h-8 text-xs bg-secondary/50 border-border focus-visible:ring-primary"
                    />
                    )}
                    {it.stage && submitting && (
                      <div data-testid="upload-item-stage" className="text-[10px] text-muted-foreground leading-tight">{it.stage}{typeof it.pct === "number" ? ` ${it.pct}%` : ""}</div>
                    )}
                    {it.error && (
                      <div data-testid="upload-item-error" className="text-[10px] text-red-300 leading-tight break-words">{it.error}</div>
                    )}
                    {!submitting && it.resume && !it.error && (
                      <div data-testid="upload-item-resume-hint" className="text-[10px] text-amber-300 leading-tight">
                        {it.resume.completed ? "Already uploaded earlier — Submit returns the existing item (no duplicate)." : `Previous attempt found${it.resume.received != null && it.resume.expected ? ` (${it.resume.received}/${it.resume.expected} parts on server)` : ""} — Submit resumes it.`}
                      </div>
                    )}
                    {!submitting && it.error && (
                      <button data-testid="upload-item-resume-btn" onClick={() => resumeOne(it)}
                        className="w-full h-7 rounded-md bg-primary/15 border border-primary/40 text-primary text-[11px] font-medium hover:bg-primary/25">
                        {it.resume ? "Resume upload (missing parts only)" : "Retry upload"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            data-testid="upload-file-input"
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />

          {items.length > 0 && !quick && (
            <Textarea
              data-testid="upload-description-input"
              placeholder="Shared description for this batch (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-secondary/50 border-border focus-visible:ring-primary resize-none"
              rows={2}
            />
          )}

          {/* Location (hidden in owner quick mode: file GPS → exact automatically, no GPS → Needs Location; nothing to decide here) */}
          {items.length > 0 && !quick && (
          <div className="rounded-lg border border-border bg-secondary/20 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground"><MapPin className="w-3.5 h-3.5 text-primary" /> Location <span className="normal-case tracking-normal text-[11px]">(applies to all)</span></div>
            {selectedLoc ? (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-card border border-border px-3 py-2" data-testid="upload-loc-selected">
                <span className="text-sm flex items-start gap-1.5 min-w-0">
                  <MapPin className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                  <span className="min-w-0">
                    <span className="block truncate" data-testid="upload-loc-selected-label">{selectedLoc.label || `${selectedLoc.city}, ${selectedLoc.country}`}</span>
                    {isAdmin && (
                      <span className="block text-[10px] text-muted-foreground truncate" data-testid="upload-loc-selected-context">
                        {(selectedLoc.path || []).map((p) => p.name).join(" › ")}{Number.isFinite(selectedLoc.lat) ? ` · ${Number(selectedLoc.lat).toFixed(4)}, ${Number(selectedLoc.lng).toFixed(4)}` : ""} · this selection wins over any GPS in the file
                      </span>
                    )}
                  </span>
                </span>
                <button data-testid="upload-loc-clear" onClick={() => { setSelectedLoc(null); setLocQuery(""); }} className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-4 h-4" /></button>
              </div>
            ) : isAdmin ? (
              <div className="space-y-1.5">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input data-testid="upload-loc-search" value={locQuery} onChange={(e) => searchLoc(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchCandidates(); } }}
                      placeholder="Type a place — YYZ, Fremont Street Las Vegas, 123 Example Street…"
                      className="w-full bg-card border border-border rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-primary" />
                  </div>
                  <Button type="button" data-testid="upload-loc-search-btn" size="sm" onClick={searchCandidates} disabled={locSearching} className="rounded-full h-9 shrink-0">
                    {locSearching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  </Button>
                </div>
                {locResults.length > 0 && (
                  <div className="space-y-1" data-testid="upload-loc-candidates">
                    <div className="text-[10px] text-muted-foreground">Which one?</div>
                    {locResults.map((c, i) => (
                      <button key={`${c.osm_type}-${c.osm_id}-${i}`} type="button" data-testid={`upload-loc-candidate-${i}`} onClick={() => pickCandidate(c)}
                        className="w-full text-left rounded-md border border-border bg-background/50 hover:border-primary hover:bg-primary/5 px-2 py-1.5 transition-colors">
                        <div className="text-[12px] text-foreground">{c.label}</div>
                        <div className="text-[10px] text-muted-foreground">{c.place && c.context ? `${c.context} · ` : ""}{(c.path || []).map((p) => p.name).join(" › ")}</div>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">Leave empty to use the GPS stored in each file (exact coordinates, labelled automatically). Picking a place here overrides it — label and coordinates.</p>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input data-testid="upload-loc-search" value={locQuery} onChange={(e) => searchLoc(e.target.value)} placeholder="Search city or country…"
                  className="w-full bg-card border border-border rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-primary" />
                {locResults.length > 0 && (
                  <div className="absolute z-[60] top-full mt-1 w-full bg-popover border border-border rounded-lg overflow-hidden max-h-48 overflow-y-auto shadow-xl">
                    {locResults.map((c) => (
                      <button key={`${c.city}-${c.cc}`} data-testid={`upload-loc-${c.cc}`} onClick={() => { setSelectedLoc(c); setLocResults([]); setLocQuery(""); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/60 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-primary" /> {c.city}, <span className="text-muted-foreground">{c.country}</span></button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {isAdmin && (
              <div className="space-y-2" data-testid="upload-library-block">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">Content Library</span>
                  <select data-testid="upload-library" value={library} onChange={(e) => setLibrary(e.target.value)}
                    className="bg-secondary/60 border border-border rounded-md text-sm px-2 py-1.5">
                    <option value="originals">BareFootage Originals</option>
                    <option value="celebrities">Celebrities</option>
                    <option value="pornstars">Pornstars</option>
                  </select>
                </div>
                {library !== "originals" && (
                  <Input data-testid="upload-named-subject" value={namedSubject} onChange={(e) => setNamedSubject(e.target.value)} placeholder="Named Subject (required) — e.g. Jane Doe" className="bg-secondary/60 border-border" />
                )}
                {library === "originals" && (
                  <label className="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-border bg-secondary/30 p-3" data-testid="upload-candid-block">
                    <Checkbox data-testid="upload-candid" checked={candid} onCheckedChange={(v) => setCandid(!!v)} className="mt-0.5" />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      <span className="text-foreground font-medium">Subject Type: candid</span> — my own candid shot. Saved as an owner tag; allows its confirmed location to appear publicly on BareFootage maps and location features. Location detection and editing work whether or not candid is selected. Unticked = no Subject Type set.
                    </span>
                  </label>
                )}
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Location visibility</span>
              <select data-testid="upload-loc-privacy" value={privacy} onChange={(e) => setPrivacy(e.target.value)}
                className="bg-card border border-border rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-primary">
                <option value="hide">Hide Location</option>
                <option value="country">Show Country</option>
                <option value="region">Show Region</option>
                <option value="city">Show City</option>
                <option value="exact">Show Exact</option>
              </select>
            </div>
            <p className="text-[11px] text-muted-foreground">GPS is auto-read from each photo when present. Exact coordinates stay private unless you choose "Show Exact".</p>
          </div>
          )}

          {items.length > 0 && !isAdmin && (
            <label className="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-border bg-secondary/30 p-3">
              <Checkbox data-testid="upload-attest" checked={attest} onCheckedChange={(v) => setAttest(!!v)} className="mt-0.5" />
              <span className="text-xs text-muted-foreground leading-relaxed">
                I confirm these are <span className="text-foreground font-medium">my own feet</span>, I am the <span className="text-foreground font-medium">woman</span> shown, I am <span className="text-foreground font-medium">18+</span>, and this is women's-feet content only.
              </span>
            </label>
          )}

          {items.length > 0 && (
            <div className="sticky bottom-0 -mx-6 px-6 pt-3 pb-1 bg-gradient-to-t from-card via-card to-transparent">
            <Button
              data-testid="upload-submit-btn"
              onClick={submit}
              disabled={submitting}
              className="w-full rounded-full font-medium"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading {progress.done}/{progress.total}…</>
                : quick ? `Upload ${items.length} ${items.length === 1 ? "item" : "items"}` : `Submit ${items.length} ${items.length === 1 ? "item" : "items"}`}
            </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


const RESOLVED = new Set(["resolved", "no_containing_venue"]);
export const locPending = (L) => !!L && L.provenance !== "owner_selected" && (L.provenance === "original_metadata" || (L.venue_resolution_status && !RESOLVED.has(L.venue_resolution_status)));

export const UploadResultCard = ({ r, onOpen }) => {
  const L = r.location;
  const coords = L && Number.isFinite(Number(L.lat)) ? `${Number(L.lat).toFixed(4)}, ${Number(L.lng).toFixed(4)}` : null;
  const pending = locPending(L);
  const prov = !L ? null : L.provenance === "owner_selected" ? "your selection" : L.provenance === "original_metadata" ? "original file GPS (place resolving)" : L.provenance === "reverse_geocode" ? "original file GPS · reverse-geocode enrichment" : L.provenance;
  return (
    <div data-testid={`upload-result-${r.id}`} className="flex gap-3 rounded-lg border border-border bg-secondary/30 p-2 text-xs">
      {r.preview && (r.isVideo ? <video src={r.preview} muted className="w-14 h-14 rounded object-cover bg-black" /> : <img src={r.preview} alt="" className="w-14 h-14 rounded object-cover bg-black" />)}
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="font-medium truncate">{r.title}</div>
        {L ? (
          <>
            <div data-testid="upload-result-location" className="text-foreground">{L.label || L.display || (pending ? "Resolving place…" : "—")}{pending && (L.label || L.display) ? <span className="ml-1 text-muted-foreground">· Resolving place…</span> : null}</div>
            {coords && <div data-testid="upload-result-coords" className="font-mono text-muted-foreground">{coords}{L.area_context?.name ? ` · ${L.area_context.name}` : ""}</div>}
            <div data-testid="upload-result-status" className="text-muted-foreground">
              {[prov ? `provenance: ${prov}` : null, r.location_public ? "public" : L.state === "needs_verification" ? "visible to you · location needs verification" : "visible to you · public once Subject Type candid is saved", L.venue_resolution_status ? `resolution: ${L.venue_resolution_status}` : null].filter(Boolean).join(" · ")}
            </div>
          </>
        ) : (
          <div data-testid="upload-result-location" className="text-muted-foreground">No GPS in the file — set the location in the editor (Needs Location)</div>
        )}
        <button type="button" onClick={onOpen} className="text-primary hover:underline" data-testid="upload-result-open">Open in editor</button>
      </div>
    </div>
  );
};
